/**
 * Per-kind upload configuration — the single source of truth for what
 * `POST /api/v1/uploads` will mint a URL for, and where the bytes end up once a
 * consuming endpoint confirms them.
 *
 * Caps are anchored to product invariants, not to the storage buckets' current
 * contents (see planning notes):
 *   - `model`  is the existing app-level cap in POST /api/v1/models, unchanged.
 *              IRs are long-tailed (0.51 MB average against a 194 MB max in the
 *              bucket), so this is deliberately not derived from the 60 s IR
 *              rule.
 *   - `audio`  is 1.94x the longest *valid* training upload — validateAudioMeta
 *              pins the format to mono/48kHz/24-bit, so the dry-wet 4:00 ceiling
 *              is 32.96 MiB. The cap only bounds staging; the real rule is the
 *              header validation at confirm time.
 *   - `image`  leaves room for raw partner uploads. The UI's own images are far
 *              smaller because it compresses via utils/imageProcessor.ts.
 */

import { STORAGE_ENV_PREFIX } from '@/utils/s3/env';
import type { SourceBucket } from '@/utils/storage/buckets';

/**
 * Where staged bytes live before a consuming endpoint confirms them:
 * `staging/<env>/<kind>/<userId>/<uuid>.<ext>`.
 *
 * The env segment is not decoration. One `api-staging` bucket and one
 * `api_uploads` table serve prod, preview and local, so it is the only thing
 * keeping a dev sweep from deleting production's in-flight uploads. Both the
 * key builder and the sweep read it from here so they cannot drift apart.
 */
export const STAGING_KEY_PREFIX = `staging/${STORAGE_ENV_PREFIX}/`;

/**
 * Abandoned uploads are collected after this long, and a handle's `expires_at`
 * is mint + this.
 *
 * Here rather than in staging.ts because the suites that exercise the sweep and
 * the resolve path mock staging.ts wholesale — a constant living there is one
 * each test restates, so the assertions check the test's own copy.
 */
export const STAGING_TTL_HOURS = 24;

/**
 * How long a claim with no placement is left alone before the sweep retires it.
 *
 * `placed_at` is a start stamp, not a deadline: the claim is written before the
 * copies run and `placed_url` only after them, so *every* placement in flight
 * is a row in exactly the state the reclaim looks for. Comparing it against
 * `now` — which is right for `expires_at` — therefore retires requests that are
 * still running, and the recordPlacedUrls that follows then violates
 * `api_uploads_url_only_when_placed` and leaves a live object with no row
 * naming it. An hour is longer than any request can live (a serverless
 * invocation is capped in minutes) and far shorter than the handle's own TTL.
 */
export const STUCK_CLAIM_GRACE_HOURS = 1;

/** What the partner API mints. */
export const UPLOAD_KINDS = ['model', 'audio', 'image'] as const;

/** What the site's own uploaders mint: the partner kinds plus avatars. */
export const SITE_UPLOAD_KINDS = [...UPLOAD_KINDS, 'avatar'] as const;

export type UploadKind = (typeof SITE_UPLOAD_KINDS)[number];

export interface UploadKindConfig {
  /** Bucket the object is copied into once a consuming endpoint confirms it. */
  bucket: SourceBucket;
  /** Lowercased, without the dot. */
  extensions: readonly string[];
  maxBytes: number;
  /**
   * How this kind's *contents* are checked before the bytes are copied into a
   * live bucket. Required, and deliberately not optional: a kind whose
   * consuming route never grew validation code would otherwise compile clean
   * and place whatever it was handed.
   *
   *   'magic-bytes' — resolveUpload checks the file's signature against its
   *                   extension. The only check images get, since nothing
   *                   downstream parses them.
   *   'downstream'  — the consuming route parses the file in full (a model's
   *                   JSON, an IR's RIFF header) and rejects it there. Name the
   *                   route in a comment alongside.
   */
  contentCheck: 'magic-bytes' | 'downstream';
}

const MIB = 1024 * 1024;

export const UPLOAD_KIND_CONFIG: Record<UploadKind, UploadKindConfig> = {
  model: {
    bucket: 'models',
    extensions: ['nam', 'wav', 'aidax', 'aasnapshot', 'json'],
    maxBytes: 256 * MIB,
    // POST /api/v1/models: JSON.parse for the model formats, parseWavMeta for
    // an IR. Both reject before anything is placed.
    contentCheck: 'downstream',
  },
  audio: {
    bucket: 'audio',
    extensions: ['wav'],
    maxBytes: 64 * MIB,
    // POST /api/v1/trainings: validateOutputAudio reads the RIFF header and
    // pins format, rate and depth.
    contentCheck: 'downstream',
  },
  image: {
    bucket: 'images',
    extensions: ['jpg', 'jpeg', 'png', 'webp'],
    maxBytes: 5 * MIB,
    contentCheck: 'magic-bytes',
  },
  avatar: {
    bucket: 'avatars',
    extensions: ['jpg', 'jpeg', 'png', 'webp'],
    maxBytes: 5 * MIB,
    contentCheck: 'magic-bytes',
  },
};

/**
 * Content type to stamp on the object when it is copied into its destination
 * bucket.
 *
 * Derived from the extension, never from the caller. The extension is checked
 * against the kind's allowlist at mint and again at resolve, so this depends on
 * nothing the client sent — which is the point. The model download endpoint
 * 302s straight to the raw public URL, so whatever the object carries is what
 * end users receive, permanently.
 *
 * The staged object's own Content-Type is therefore ignored, not trusted. We
 * could pin it — `signableHeaders: new Set(['content-type'])` overrides the
 * presigner's default and puts it in SignedHeaders — but that would only make
 * storage 403 a client whose header disagrees (`curl --upload-file` sends
 * `application/x-www-form-urlencoded`), and we would still rewrite here.
 */
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  nam: 'application/json',
  aidax: 'application/json',
  aasnapshot: 'application/json',
  json: 'application/json',
  wav: 'audio/wav',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

/**
 * Extensions capped below their kind.
 *
 * Empty, deliberately. `POST /api/v1/models` has enforced a flat 256 MiB since
 * it shipped, and partners integrate against that number — narrowing it per
 * extension would start returning 413 on files the API accepts today, for a
 * ceiling no file in the bucket has ever come near (`.nam` runs 602,916 files
 * at a 0.48 MiB p99 with a 52.19 MiB max).
 *
 * The seam stays because the risk it was added for is real: the JSON-bearing
 * model formats are parsed in full, so their true ceiling is what a
 * function's heap can take, not what the bucket can. Tightening them is a
 * deliberate, announced change — not a side effect of adding an upload path.
 */
const MAX_BYTES_BY_EXTENSION: Record<string, number> = {};

/** The binding cap for one upload: the tighter of the kind's and the extension's. */
export function maxBytesFor(kind: UploadKind, extension: string): number {
  const kindMax = UPLOAD_KIND_CONFIG[kind].maxBytes;
  const extensionMax = MAX_BYTES_BY_EXTENSION[extension];
  return extensionMax != null ? Math.min(kindMax, extensionMax) : kindMax;
}

/** One wording for the 413, so mint time and confirm time cannot disagree. */
export function sizeLimitError(kind: UploadKind, extension: string): string {
  const max = maxBytesFor(kind, extension);
  const scope = max < UPLOAD_KIND_CONFIG[kind].maxBytes ? `.${extension}` : kind;
  return `File exceeds the ${Math.floor(max / MIB)} MiB limit for ${scope} uploads`;
}

export function isUploadKind(value: unknown): value is UploadKind {
  return typeof value === 'string' && (SITE_UPLOAD_KINDS as readonly string[]).includes(value);
}

/** Lowercased extension without the dot, or null when there isn't one. */
export function extensionOf(filename: string): string | null {
  const base = filename.split('/').pop() ?? filename;
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return null;
  return base.slice(dot + 1).toLowerCase();
}

export function contentTypeFor(extension: string): string {
  return CONTENT_TYPE_BY_EXTENSION[extension] ?? 'application/octet-stream';
}
